import { useState } from "react"
import { Link } from "expo-router"
import { ActivityIndicator, Pressable, View } from "react-native"
import { SafeAreaView } from "react-native-safe-area-context"
import { useDebounce } from "use-debounce"
import { useObservable, Show } from "@legendapp/state/react"

import { ar } from "@src/i18n"
import { useNumCols } from "@src/hooks"
import { formatPrice, getScaleCode, sharedStyles, trpc, ENV } from "@src/utils"
import { formatWeight } from "@white-labels/utils"
import { List, Header, Text, TextInput, MCIcon, Image } from "@src/components"

export default function DeletedProducts() {
  const [search, setSearch] = useState("")
  const [debouncedSearch] = useDebounce(search, 500)
  const state$ = useObservable({ search: false })

  const numCols = Math.min(4, Math.max(2, useNumCols(250, { maxWidth: 1400 })))
  const products = trpc.products.list.useInfiniteQuery({ search: debouncedSearch, limit: 50, status: ["inactive"] }, { getNextPageParam: (lastPage) => lastPage.nextCursor })

  function toggleSearch() {
    state$.set({ search: !state$.search.get() })
    setSearch("")
  }

  function hideSearch() {
    state$.set({ search: false })
    setSearch("")
  }

  return (
    <>
      <Header
        title={ar.deletedProducts}
        right={
          <View className="flex-row h-full">
            <Pressable onPress={toggleSearch} className="px-2 h-full justify-center active:opacity-70">
              <MCIcon name="magnify" size={24} />
            </Pressable>
          </View>
        }
      />

      <View className="flex-1">
        <Show if={state$.search}>
          <View className="relative max-w-content mx-2 md:mx-auto md:w-full my-2">
            <TextInput value={search} onChangeText={setSearch} autoFocus />
            <Pressable className="absolute px-2 top-0 bottom-0 left-0 justify-center items-center active:opacity-70" onPress={hideSearch}>
              <MCIcon name="close" size={24} />
            </Pressable>
          </View>
        </Show>

        <List
          contentContainerStyle={sharedStyles.contentContainer}
          data={products.data?.pages.flatMap((page) => page.items) ?? []}
          numColumns={numCols}
          key={`deleted-products-${numCols}`}
          keyExtractor={(item) => item.id.toString()}
          onEndReached={() => (products.hasNextPage ? products.fetchNextPage() : undefined)}
          onRefresh={products.refetch}
          onEndReachedThreshold={0.8}
          ListEmptyComponent={products.isLoading ? <ActivityIndicator className="py-8" /> : <Text className="text-gray-500 text-center py-8">{ar.noProducts}</Text>}
          renderItem={({ item }) => {
            const showOriginalPrice = item.originalPrice && item.originalPrice > item.price
            const image = item.images?.at(-1)

            return (
              <View className="bg-white p-2 rounded mb-2 mx-1 flex-1 border border-red-200 opacity-70">
                <Link href={`/dashboard/products/${item.type === "WEIGHT_DEFINED_PRODUCT" ? item.baseProductId : item.id}`} asChild>
                  <Pressable className="active:opacity-70">
                    {image ? (
                      <Image
                        contentFit="contain"
                        cachePolicy="memory"
                        source={`${ENV.R2_URL}/${image.key}`}
                        className="w-full h-28 self-center mb-2"
                        recyclingKey={image.key}
                        transition={250}
                        loading="lazy"
                      />
                    ) : (
                      <View className="w-full h-28 bg-gray-100 rounded items-center justify-center mb-2">
                        <MCIcon name="image" size={40} color="#9CA3AF" />
                      </View>
                    )}

                    <Text className="text-sm font-semibold text-gray-900 text-center leading-tight mb-2" numberOfLines={2}>
                      {item.name}
                    </Text>

                    <Text className="text-xs text-gray-500 text-center mb-1 bg-gray-100 py-1">{item.upc}</Text>

                    <View className="items-center">
                      <Text className="text-base md:text-lg font-bold text-green-600">
                        {formatPrice(item.price)} {ar.egp}
                      </Text>
                      {showOriginalPrice ? (
                        <Text className="text-xs md:text-sm text-gray-500 line-through mt-0.5">
                          {formatPrice(item.originalPrice)} {ar.egp}
                        </Text>
                      ) : null}
                    </View>

                    {item.type === "WEIGHT_PRODUCT" || item.type === "WEIGHT_DEFINED_PRODUCT" ? (
                      <View className="absolute -top-1 -end-1 flex-row gap-1">
                        <View className="bg-green-400 px-2">
                          <Text className="text-white text-sm font-bold">{item.type === "WEIGHT_DEFINED_PRODUCT" ? formatWeight(item.value ?? 0, ar.kg, ar.gms) : ar.weight}</Text>
                        </View>
                        <View className="bg-blue-400 px-2">
                          <Text className="text-white text-sm font-bold">{getScaleCode(item.upc)}</Text>
                        </View>
                      </View>
                    ) : null}

                    <View className="absolute top-0 start-0 bg-red-500 px-2 py-0.5 rounded-br">
                      <Text className="text-white text-xs font-bold">{ar.deleted}</Text>
                    </View>
                  </Pressable>
                </Link>
              </View>
            )
          }}
          ListFooterComponent={<SafeAreaView edges={["bottom"]} />}
        />
      </View>
    </>
  )
}
